import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_ACCESS_ROLES = ['super_admin', 'admin', 'tenant_admin', 'owner'];
// Platform Console capabilities are Cloverleaf-operator only and must NOT be
// granted by the customer-admin short-circuit — they are checked against the
// actual permission list (only super_admin's role bundle carries them).
const PLATFORM_CAP_PREFIXES = ['platform:', 'support_access:'];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [permsLoaded, setPermsLoaded] = useState(false);
  // Platform-wide config the app needs BEFORE auth (root_page). Fetched from a
  // public endpoint on mount; defaults to the marketing landing page.
  const [appConfig, setAppConfig] = useState({ root_page: 'landing', login_bg_image_url: '', login_tagline: '' });
  const [appConfigLoaded, setAppConfigLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/public/app-config`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (alive && data?.root_page) setAppConfig({
            root_page: data.root_page,
            login_bg_image_url: data.login_bg_image_url || '',
            login_tagline: data.login_tagline || '',
            system_branding: data.system_branding || null,
          });
        }
      } catch {
        /* best-effort — fall back to the landing default */
      } finally {
        if (alive) setAppConfigLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  // checkAuth is called both on mount and after login for trial-state
  // enrichment. Only an explicit 401 should clear the user — transient
  // network errors or 5xx must NOT log the user out, otherwise a brief
  // hiccup right after login flashes the home screen and bounces back
  // to /login (classic race between the cookie being written and the
  // enrichment fetch firing).
  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        try {
          if (data?.role !== 'admin' && data?.role !== 'super_admin') window.localStorage.removeItem('inzara.active_workspace_id');
        } catch (_) { /* storage disabled */ }
        loadPerms();
      } else if (res.status === 401 || res.status === 403) {
        setUser(null);
        setPermissions([]);
        setPermsLoaded(true);
      }
      // Any other status: keep whatever user state we already have.
    } catch {
      // Network failure — don't log the user out, they may already be
      // authenticated locally and we'll retry on next navigation.
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the signed-in user's effective RBAC permissions. Used to hide nav
  // items and block pages the user's role can't access.
  const loadPerms = useCallback(async () => {
    try {
      const r = await fetch(`${API}/rbac/my-permissions`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setPermissions(Array.isArray(d.permissions) ? d.permissions : []);
      }
    } catch {
      /* best-effort */
    } finally {
      setPermsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // FastAPI may return detail as a string OR an array of {msg} (422) — and a
  // failed proxy can return a null/non-JSON body. Never assume shape.
  const detailMsg = (d) => {
    const detail = d?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : '')).filter(Boolean).join(' ');
    return '';
  };

  const login = async (email, password, viaSlug = null) => {
    let res;
    try {
      res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, ...(viaSlug ? { via_slug: viaSlug } : {}) }),
      });
    } catch (err) {
      throw new Error('Unable to connect to server. Please try again.');
    }
    const data = (await res.json().catch(() => null)) ?? {};
    if (!res.ok) throw new Error(detailMsg(data) || `Sign-in failed (error ${res.status}). Please try again — if this keeps happening, contact support.`);
    setUser(data.user);
    // Clear any leftover workspace selection for non-admins — the workspace
    // switcher only applies to admins, and a stale value in this browser
    // (e.g. left by a prior admin session) would otherwise be forwarded as
    // X-Workspace-Id and scope the non-admin out of their own content.
    try {
      const r = data.user?.role;
      if (r !== 'admin' && r !== 'super_admin') window.localStorage.removeItem('inzara.active_workspace_id');
    } catch (_) { /* storage disabled */ }
    loadPerms();
    // Fire-and-forget enrichment of the user record with tenant trial
    // state. checkAuth is now guarded to only clear the user on an
    // explicit 401/403, so a transient network hiccup here can't bounce
    // the newly-signed-in user back to the login screen.
    checkAuth();
    return data;
  };

  const register = async (email, password, name) => {
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name }),
      });
      const data = (await res.json().catch(() => null)) ?? {};
      if (!res.ok) throw new Error(detailMsg(data) || 'Registration failed');
      setUser(data.user);
      return data;
    } catch (err) {
      if (err.message.includes('clone') || err.message.includes('stream') || err.message.includes('body')) {
        throw new Error('Please try again');
      }
      throw err;
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
  };

  // Partial user updates (profile save, avatar change, etc.) come back from
  // endpoints that return the raw user doc WITHOUT the session-only
  // `impersonated_by` marker. Carry it forward so those updates never make the
  // impersonation banner vanish. Ending impersonation always does a full page
  // reload (Layout "Exit"), so this can never keep the flag around wrongly.
  const setUserData = (userData) => setUser(prev => {
    if (userData && prev?.impersonated_by && !userData.impersonated_by) {
      return { ...userData, impersonated_by: prev.impersonated_by };
    }
    return userData;
  });

  const hasPermission = useCallback((perm) => {
    if (!perm) return true;
    const isPlatformCap = PLATFORM_CAP_PREFIXES.some((p) => perm.startsWith(p));
    if (!isPlatformCap && user && ALL_ACCESS_ROLES.includes(user.role)) return true;
    return permissions.includes(perm);
  }, [permissions, user]);

  return (
    <AuthContext.Provider value={{ user, loading, appConfig, appConfigLoaded, setAppConfig, login, register, googleLogin, logout, checkAuth, setUserData, permissions, permsLoaded, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
